/**
 * Decompression Utilities — base64 + gzip decompression with fallbacks.
 */

import pako from "pako";

/**
 * Decompresses a base64-encoded, gzip-compressed string.
 * @param {string} base64Compressed - Base64 encoded gzip compressed data
 * @returns {string|null} Decompressed string or null on failure.
 */
export const decompressResponse = (base64Compressed) => {
  try {
    if (!base64Compressed || typeof base64Compressed !== "string") return null;

    let trimmed = base64Compressed.trim();
    if (!trimmed) return null;

    // Unwrap a single JSON string layer (e.g. "\"H4sI...\"").
    if (trimmed[0] === "\"") {
      try {
        const unwrapped = JSON.parse(trimmed);
        if (typeof unwrapped === "string") trimmed = unwrapped.trim();
      } catch { /* not JSON — proceed */ }
      if (!trimmed) return null;
    }

    // Fast-path plain JSON bodies.
    if (trimmed[0] === "{" || trimmed[0] === "[") return null;

    // Normalize base64url → base64, strip whitespace.
    const cleaned = trimmed.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");

    // Reject non-base64.
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleaned)) return null;

    // Fix missing padding.
    const remainder = cleaned.length % 4;
    if (remainder === 1) return null;
    const padded = remainder === 0 ? cleaned : cleaned + "=".repeat(4 - remainder);

    // Base64 decode → binary string → Uint8Array.
    const binaryString = atob(padded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);

    // Decompress with gzip; fallback to raw UTF-8 if not gzip.
    let decompressed;
    try {
      decompressed = pako.ungzip(bytes, { to: "string" }) ?? null;
    } catch {
      // Not gzip — try raw decode.
      if (typeof TextDecoder !== "undefined") {
        const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes).trim();
        if (decoded && (decoded[0] === "{" || decoded[0] === "[")) return decoded;
      }
      return null;
    }
    if (decompressed) return decompressed;

    // pako.ungzip returned null/undefined (truncated data) — try raw decode.
    if (typeof TextDecoder !== "undefined") {
      const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes).trim();
      if (decoded && (decoded[0] === "{" || decoded[0] === "[")) return decoded;
    }
    return null;
  } catch (error) {
    if (error?.name === "InvalidCharacterError") return null;
    console.error("Decompression error:", error);
    return null;
  }
};
